import React from "react";
import styled from "styled-components";
import Image from "next/image";
import Button from "components/Button";
import GlobalFooter from "components/GlobalFooter";
import useWindowSize from "hooks/use-window-size";

const HeroWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 3rem 1.5rem 3rem;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    padding: 5rem 2rem 5rem;
  `}

  ${({ theme }) => theme.mq.xl`
    padding: 6.75rem 1.5rem 6.75rem;
  `}
`;

const InnerWrapper = styled.div`
  margin: 0 auto;
  max-width: 42.5rem;

  ${({ theme }) => theme.mq.lg`
    display:flex;
    justify-content: space-between;
    max-width: 77rem;
  `}
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  line-height: 1.1;

  ${({ theme }) => theme.mq.lg`
    font-size: 3.8125rem;
  `}
`;

const HeroSummary = styled.p`
  font-size: 1.125rem;
  font-weight: 400;
  line-height: 1.388;
  margin: 0 0 1rem;

  ${({ theme }) => theme.mq.lg`
    font-size: 1.25rem;
    max-width: 30.25rem;
  `}
`;

const ImageWrapper = styled.div`
  align-self: center;
  height: calc((100vw - 4rem) / (75 / 49));
  margin: 2.5rem auto 0;
  padding: 0;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.lg`
    height: 28.875rem;
    margin: 2.4375rem 0 1.125rem 1.5rem;
    max-width: 50vw;
    width: 44.25rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 2.4375rem -2rem 1.125rem 1.5rem;
  `}
`;

const BrandsWrapper = styled.div`
  background-color: #24345b;
  color: #fff;
  margin: 0 auto;
  padding: 0 0 3.75rem;
  width: 100vw;

  ${({ theme }) => theme.mq.lg`
    display: flex;
    padding: 0;
  `}
`;

const BrandImage = styled.div`
  height: calc((100vw) / (118 / 85));
  margin: 0 auto 2rem;
  position: relative;
  width: 100vw;

  ${({ theme }) => theme.mq.lg`
    height: calc((50vw) / (118 / 85));
    max-height: 40rem;
    min-height: 32.6875rem;
    margin: 0 auto;
    width: 52vw;
  `}

  ${({ theme }) => theme.mq.xxl`
    width: 52vw;
  `}
`;

const Brand = styled.div`
  margin: 0 1.5rem;

  ${({ theme }) => theme.mq.lg`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 48vw;
  `}

  ${({ theme }) => theme.mq.xxl`
      margin: 0 0 0 5.625rem;
    width: 48vw;
  `}
`;

const PetExecLogo = styled.div`
  position: relative;
  margin: 0 0 1.5rem;

  ${({ theme }) => theme.mq.lg`
    margin: 0 0 1.5rem;
  `}
`;

const BrandSummary = styled(HeroSummary)`
  line-height: 1.388;
  max-width: 35.5rem;
`;

const ButtonStyled = styled(Button)`
  background-color: #fff;
  color: #000;
  font-size: 1.125rem;
  height: 3.375rem;
  margin: 1.5rem 0 0;
  width: 11.5rem;
`;

const HelpWrapper = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.themeColors.neutral};
  display: flex;
  flex-direction: column;
  height: 15.625rem;
  justify-content: center;
  padding: 0 1.5rem;
  text-align: center;

  ${({ theme }) => theme.mq.lg`
    height: 21.75rem;
  `}
`;

const HelpTitle = styled.h2`
  width: 20.3125rem;

  ${({ theme }) => theme.mq.lg`
    font-size: 3rem;
    width: 48rem;
  `}
`;

const HelpSummary = styled(HeroSummary)`
  a {
    font-size: 1.25rem;
    font-weight: 400;
    text-decoration: underline;
  }
`;

export default function ProviderSplashPage() {
  const { width } = useWindowSize();
  return (
    <>
      <HeroWrapper>
        <InnerWrapper>
          <div>
            <HeroTitle>Thanks for using GoodFriend!</HeroTitle>
            <HeroSummary>
              We've made some good friends, seen some great Poodle cuts, and learned a lot about pet care. Thanks for being a part of it!
            </HeroSummary>
            <HeroSummary>
              But, like an Afghan Hound in a grooming sesh, GoodFriend is evolving. So we're saying goodbye for now while we work on new
              ways to help people care for pets. (More to come on that!)
            </HeroSummary>
            <HeroSummary>While we're away, check out our sister brand, PetExec.</HeroSummary>
          </div>
          <div>
            <ImageWrapper>
              <Image
                alt="Illustration of pets and parents playing in a park in front of a GoodFriend groomer"
                layout="fill"
                src="/hero_illustration.png"
                objectFit="contain"
                priority
              />
            </ImageWrapper>
          </div>
        </InnerWrapper>
      </HeroWrapper>
      <BrandsWrapper>
        <BrandImage>
          <Image
            alt="Person lovingly holding holding their dog while drinking coffee and looking a laptop computer on a patio"
            layout="fill"
            objectFit="cover"
            src="/person_drinking_coffee_with_dog.png"
          />
        </BrandImage>
        <Brand>
          <PetExecLogo>
            <Image
              alt="Person lovingly holding holding dog while drinking coffee and looking a laptop computer on patio"
              height={width > 992 ? 86 : 56}
              width={width > 992 ? 300 : 200}
              src="/pet_exec.png"
            />
          </PetExecLogo>
          <BrandSummary>
            PetExec is the leading tool for pet care businesses, helping teams of all sizes more efficiently manage their daycare, boarding,
            grooming, and training operations. So you can spend more time on pets.
          </BrandSummary>
          <BrandSummary>In other words? They’re like a 24/7 ops manager. So try them out.</BrandSummary>
          <a
            href="https://www.petexec.net/?utm_medium=referral&utm_source=goodfriend&utm_campaign=goodfriend_site"
            target="blank"
          >
            <ButtonStyled>Learn More</ButtonStyled>
          </a>
        </Brand>
      </BrandsWrapper>
      <HelpWrapper>
        <HelpTitle>Need anything? We’re here to help.</HelpTitle>
        <HelpSummary>
          Email us <a href="mailto:prohelp@goodfriend.com">here</a>.
        </HelpSummary>
      </HelpWrapper>
      <GlobalFooter />
    </>
  );
}
